import { useEffect, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";

// Searchable picker over /people; value is a person id.
export function PersonPicker({ value, onChange, disabled, personType, testId = "person-picker", placeholder = "Select a person" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!value) return setSelected(null);
    if (selected?.id === value) return;
    api.get(`/people/${value}`).then((r) => setSelected(r.data)).catch(() => setSelected(null));
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      const params = { page_size: 20, ...(q ? { q } : {}), ...(personType ? { person_type: personType } : {}) };
      api.get("/people", { params }).then((r) => setItems(r.data.items));
    }, 200);
    return () => clearTimeout(t);
  }, [q, open, personType]);

  const label = (p) => `${p.first_name} ${p.last_name}`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button type="button" data-testid={testId}
          className="w-full flex items-center justify-between rounded-md border border-slate-300 bg-white px-3 py-2 text-sm disabled:opacity-60">
          <span className={selected ? "text-slate-800" : "text-slate-400"}>{selected ? label(selected) : placeholder}</span>
          <span className="flex items-center gap-1">
            {selected && !disabled && (
              <X className="h-4 w-4 text-slate-400 hover:text-slate-700" data-testid={`${testId}-clear`}
                onClick={(e) => { e.stopPropagation(); onChange(null); }} />
            )}
            <ChevronsUpDown className="h-4 w-4 text-slate-400" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-2 w-[--radix-popover-trigger-width]" align="start">
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…" data-testid={`${testId}-search`}
          className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm mb-2 outline-none focus:ring-2 focus:ring-blue-600" />
        <div className="max-h-60 overflow-y-auto">
          {items.map((p) => (
            <button type="button" key={p.id} data-testid={`${testId}-option-${p.id}`}
              onClick={() => { setSelected(p); onChange(p.id); setOpen(false); }}
              className="w-full flex items-center justify-between text-left px-2 py-1.5 rounded text-sm hover:bg-blue-50">
              <span><span className="text-slate-800">{label(p)}</span> <span className="text-xs text-slate-400">{p.email}</span></span>
              {value === p.id && <Check className="h-4 w-4 text-blue-600" />}
            </button>
          ))}
          {!items.length && <div className="text-xs text-slate-400 p-2">No people found</div>}
        </div>
      </PopoverContent>
    </Popover>
  );
}
